import React, { createContext, useMemo, useEffect, useContext } from 'react';
import { jsxs, jsx } from 'react/jsx-runtime';

var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);

// src/theme/colors.ts
var colors = {
  // Brand colors
  brand: {
    primary: "#A8B0BC",
    // Platinum
    secondary: "#8891A0",
    // Silver
    accent: "#6B7B94",
    // Slate (interactive elements)
    danger: "#C96B6B",
    warning: "#D4A84A",
    info: "#6BA3D4"
  },
  // Neutral colors (dark-first: 50 = darkest background, 900 = lightest text)
  neutral: {
    50: "#08090C",
    // Void — deepest background
    100: "#0E1014",
    // Obsidian — primary background
    200: "#15171C",
    // Graphite — nav, elevated bg
    300: "#1C1E25",
    // Surface — cards, panels
    400: "#24262E",
    // Surface 2 — inputs, hover
    500: "#2D3039",
    // Surface 3 — active states
    600: "#8A8F9E",
    // Grey 300 — secondary text
    700: "#B8BCC8",
    // Grey 200 — body text
    800: "#D5D8E0",
    // Grey 100 — high-contrast body
    900: "#ECEEF2"
    // White — primary text on dark
  },
  // Semantic colors
  semantic: {
    success: "#5CC9A0",
    error: "#C96B6B",
    warning: "#D4A84A",
    info: "#6BA3D4"
  },
  // Additional color scales for theming (platinum/silver system)
  primary: {
    50: "#1A2133",
    // Ink
    100: "#2A3650",
    // Ink Light
    200: "#3A3E4A",
    // Grey 500
    300: "#5C6170",
    // Grey 400
    400: "#6B7B94",
    // Slate
    500: "#A8B0BC",
    // Platinum (brand.primary)
    600: "#8494AD",
    // Slate Light
    700: "#C4CAD4",
    // Silver Light
    800: "#D8DDE6",
    // Chrome
    900: "#ECEEF2"
    // White
  },
  secondary: {
    50: "#1A2133",
    // Ink
    100: "#24262E",
    // Surface 2
    200: "#3A3E4A",
    // Grey 500
    300: "#5C6170",
    // Grey 400
    400: "#6B7B94",
    // Slate
    500: "#8891A0",
    // Silver (brand.secondary)
    600: "#A8B0BC",
    // Platinum
    700: "#C4CAD4",
    // Silver Light
    800: "#D8DDE6",
    // Chrome
    900: "#ECEEF2"
    // White
  }
};

// src/theme/typography.ts
var typography = {
  // Font stack per brand-identity.html: Playfair Display (display/headings),
  // Sora (body), IBM Plex Mono (code/labels/mono). This package does not load
  // the Google Fonts <link> itself (that's the consuming app's job — see
  // console/web/index.html) — these stacks include system-font fallbacks so
  // components render sanely before/without the webfonts loading.
  fontFamily: {
    display: '"Playfair Display", Georgia, "Times New Roman", serif',
    sans: '"Sora", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    mono: '"IBM Plex Mono", "Fira Code", "Consolas", "Courier New", monospace'
  },
  fontSize: {
    xs: "0.75rem",
    // 12px
    sm: "0.875rem",
    // 14px
    base: "1rem",
    // 16px
    lg: "1.125rem",
    // 18px
    xl: "1.25rem",
    // 20px
    "2xl": "1.5rem",
    // 24px
    "3xl": "1.875rem",
    // 30px
    "4xl": "2.25rem",
    // 36px
    "5xl": "3rem"
    // 48px
  },
  fontWeight: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700
  },
  lineHeight: {
    tight: 1.25,
    normal: 1.5,
    relaxed: 1.75
  },
  letterSpacing: {
    tighter: "-0.05em",
    tight: "-0.025em",
    normal: "0em",
    wide: "0.025em",
    wider: "0.05em",
    widest: "0.1em"
  }
};

// src/theme/spacing.ts
var spacing = {
  0: "0",
  1: "0.25rem",
  // 4px
  2: "0.5rem",
  // 8px
  3: "0.75rem",
  // 12px
  4: "1rem",
  // 16px
  5: "1.25rem",
  // 20px
  6: "1.5rem",
  // 24px
  8: "2rem",
  // 32px
  10: "2.5rem",
  // 40px
  12: "3rem",
  // 48px
  16: "4rem",
  // 64px
  20: "5rem",
  // 80px
  24: "6rem",
  // 96px
  32: "8rem",
  // 128px
  40: "10rem",
  // 160px
  48: "12rem",
  // 192px
  56: "14rem",
  // 224px
  64: "16rem"
  // 256px
};
var borderRadius = {
  none: "0",
  sm: "0.125rem",
  // 2px
  base: "0.25rem",
  // 4px
  md: "0.375rem",
  // 6px
  lg: "0.5rem",
  // 8px
  xl: "0.75rem",
  // 12px
  "2xl": "1rem",
  // 16px
  "3xl": "1.5rem",
  // 24px
  full: "9999px"
};

// src/theme/tokens.ts
var tokens = {
  colors,
  typography,
  spacing,
  borderRadius,
  // Named spacing scale from brand-identity.html (--sp-xs..--sp-5xl, 4px
  // base). Distinct from the numeric `spacing` scale above (which many
  // components already reference via Tailwind arbitrary values) — this is
  // the brand doc's own named scale, exposed for consumers that want to
  // match it directly (e.g. `var(--sp-lg)`).
  brandSpacing: {
    xs: "4px",
    sm: "8px",
    md: "16px",
    lg: "24px",
    xl: "32px",
    "2xl": "48px",
    "3xl": "64px",
    "4xl": "96px",
    "5xl": "128px"
  },
  // Brand radii from brand-identity.html (--radius-sm/md/lg), distinct from
  // the generic `borderRadius` scale above.
  brandRadius: {
    sm: "3px",
    md: "6px",
    lg: "10px"
  },
  // Shadows
  shadows: {
    sm: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
    base: "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
    md: "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
    lg: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
    xl: "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
    "2xl": "0 25px 50px -12px rgb(0 0 0 / 0.25)",
    inner: "inset 0 2px 4px 0 rgb(0 0 0 / 0.05)",
    none: "none"
  },
  // Brand shadows from brand-identity.html (--shadow-sm/md/lg/platinum).
  brandShadows: {
    sm: "0 1px 3px rgba(0,0,0,0.30)",
    md: "0 4px 16px rgba(0,0,0,0.35)",
    lg: "0 12px 48px rgba(0,0,0,0.40)",
    platinum: "0 4px 24px rgba(168,176,188,0.06)"
  },
  // Transitions
  transitions: {
    duration: {
      fast: "150ms",
      base: "200ms",
      slow: "300ms",
      slower: "500ms"
    },
    timing: {
      easeIn: "cubic-bezier(0.4, 0, 1, 1)",
      easeOut: "cubic-bezier(0, 0, 0.2, 1)",
      easeInOut: "cubic-bezier(0.4, 0, 0.2, 1)",
      linear: "linear"
    }
  },
  // Breakpoints
  breakpoints: {
    sm: "640px",
    md: "768px",
    lg: "1024px",
    xl: "1280px",
    "2xl": "1536px"
  },
  // Z-index scale
  zIndex: {
    base: 0,
    dropdown: 1e3,
    sticky: 1100,
    fixed: 1200,
    modalBackdrop: 1300,
    modal: 1400,
    popover: 1500,
    tooltip: 1600
  },
  // Border widths
  borderWidth: {
    0: "0",
    1: "1px",
    2: "2px",
    4: "4px",
    8: "8px"
  },
  // Opacity scale
  opacity: {
    0: "0",
    5: "0.05",
    10: "0.1",
    20: "0.2",
    30: "0.3",
    40: "0.4",
    50: "0.5",
    60: "0.6",
    70: "0.7",
    80: "0.8",
    90: "0.9",
    95: "0.95",
    100: "1"
  }
};

// src/theme/themes.ts
var peachMintCreamTheme = {
  id: "peach-mint-cream",
  name: "Peach Mint Cream",
  description: "Soft and inviting with warm peach and cool mint tones",
  icon: "\u{1F351}",
  colors: {
    brand: {
      primary: "#ff9a76",
      // Soft peach
      secondary: "#7bc9a6",
      // Mint green
      accent: "#ffd4a3",
      // Cream peach
      danger: "#ff6b6b",
      // Coral red
      warning: "#feca57",
      // Warm yellow
      info: "#48dbfb"
      // Light blue
    },
    neutral: {
      50: "#fffcf7",
      // Cream white
      100: "#fef6ed",
      // Light cream
      200: "#fde8d5",
      // Soft cream
      300: "#f8d1b0",
      // Peachy cream
      400: "#e0b590",
      // Tan
      500: "#c49a7a",
      // Warm brown
      600: "#a07e64",
      // Medium brown
      700: "#7d624e",
      // Dark brown
      800: "#5a4738",
      // Deep brown
      900: "#3d2f25"
      // Almost black brown
    },
    semantic: {
      success: "#7bc9a6",
      // Mint green
      error: "#ff6b6b",
      // Coral red
      warning: "#feca57",
      // Warm yellow
      info: "#48dbfb"
      // Light blue
    },
    primary: {
      50: "#fff5f0",
      100: "#ffe8dc",
      200: "#ffd1b8",
      300: "#ffba94",
      400: "#ffa385",
      500: "#ff9a76",
      600: "#ff8c67",
      700: "#ff7e58",
      800: "#ff7049",
      900: "#ff623a"
    },
    secondary: {
      50: "#f0fdf7",
      100: "#e0faf0",
      200: "#c1f5e1",
      300: "#a2f0d2",
      400: "#8bdfc0",
      500: "#7bc9a6",
      600: "#6bb396",
      700: "#5b9d86",
      800: "#4b8776",
      900: "#3b7166"
    }
  }
};
var indigoTheme = {
  id: "indigo",
  name: "Indigo",
  description: "Clean and professional indigo theme",
  icon: "\u{1F4BC}",
  colors: {
    brand: {
      primary: "#6366f1",
      secondary: "#8b5cf6",
      accent: "#10b981",
      danger: "#ef4444",
      warning: "#f59e0b",
      info: "#3b82f6"
    },
    neutral: {
      50: "#f9fafb",
      100: "#f3f4f6",
      200: "#e5e7eb",
      300: "#d1d5db",
      400: "#9ca3af",
      500: "#6b7280",
      600: "#4b5563",
      700: "#374151",
      800: "#1f2937",
      900: "#111827"
    },
    semantic: {
      success: "#10b981",
      error: "#ef4444",
      warning: "#f59e0b",
      info: "#3b82f6"
    },
    primary: {
      50: "#eef2ff",
      100: "#e0e7ff",
      200: "#c7d2fe",
      300: "#a5b4fc",
      400: "#818cf8",
      500: "#6366f1",
      600: "#4f46e5",
      700: "#4338ca",
      800: "#3730a3",
      900: "#312e81"
    },
    secondary: {
      50: "#faf5ff",
      100: "#f3e8ff",
      200: "#e9d5ff",
      300: "#d8b4fe",
      400: "#c084fc",
      500: "#a855f7",
      600: "#9333ea",
      700: "#7e22ce",
      800: "#6b21a8",
      900: "#581c87"
    }
  }
};
var oceanTheme = {
  id: "ocean",
  name: "Ocean",
  description: "Serene blue theme inspired by the deep sea",
  icon: "\u{1F30A}",
  colors: {
    brand: {
      primary: "#0ea5e9",
      secondary: "#06b6d4",
      accent: "#14b8a6",
      danger: "#f43f5e",
      warning: "#f59e0b",
      info: "#3b82f6"
    },
    neutral: {
      50: "#f0f9ff",
      100: "#e0f2fe",
      200: "#bae6fd",
      300: "#7dd3fc",
      400: "#38bdf8",
      500: "#0ea5e9",
      600: "#0284c7",
      700: "#0369a1",
      800: "#075985",
      900: "#0c4a6e"
    },
    semantic: {
      success: "#14b8a6",
      error: "#f43f5e",
      warning: "#f59e0b",
      info: "#0ea5e9"
    },
    primary: {
      50: "#f0f9ff",
      100: "#e0f2fe",
      200: "#bae6fd",
      300: "#7dd3fc",
      400: "#38bdf8",
      500: "#0ea5e9",
      600: "#0284c7",
      700: "#0369a1",
      800: "#075985",
      900: "#0c4a6e"
    },
    secondary: {
      50: "#ecfeff",
      100: "#cffafe",
      200: "#a5f3fc",
      300: "#67e8f9",
      400: "#22d3ee",
      500: "#06b6d4",
      600: "#0891b2",
      700: "#0e7490",
      800: "#155e75",
      900: "#164e63"
    }
  }
};
var forestTheme = {
  id: "forest",
  name: "Forest",
  description: "Earthy green theme for natural interfaces",
  icon: "\u{1F332}",
  colors: {
    brand: {
      primary: "#10b981",
      secondary: "#059669",
      accent: "#84cc16",
      danger: "#dc2626",
      warning: "#f59e0b",
      info: "#3b82f6"
    },
    neutral: {
      50: "#f0fdf4",
      100: "#dcfce7",
      200: "#bbf7d0",
      300: "#86efac",
      400: "#4ade80",
      500: "#22c55e",
      600: "#16a34a",
      700: "#15803d",
      800: "#166534",
      900: "#14532d"
    },
    semantic: {
      success: "#10b981",
      error: "#dc2626",
      warning: "#f59e0b",
      info: "#3b82f6"
    },
    primary: {
      50: "#f0fdf4",
      100: "#dcfce7",
      200: "#bbf7d0",
      300: "#86efac",
      400: "#4ade80",
      500: "#22c55e",
      600: "#16a34a",
      700: "#15803d",
      800: "#166534",
      900: "#14532d"
    },
    secondary: {
      50: "#f0fdf4",
      100: "#dcfce7",
      200: "#bbf7d0",
      300: "#86efac",
      400: "#4ade80",
      500: "#22c55e",
      600: "#16a34a",
      700: "#15803d",
      800: "#166534",
      900: "#14532d"
    }
  }
};
var sunsetTheme = {
  id: "sunset",
  name: "Sunset",
  description: "Warm orange and pink theme with vibrant energy",
  icon: "\u{1F305}",
  colors: {
    brand: {
      primary: "#f97316",
      secondary: "#ec4899",
      accent: "#f59e0b",
      danger: "#dc2626",
      warning: "#f59e0b",
      info: "#3b82f6"
    },
    neutral: {
      50: "#fff7ed",
      100: "#ffedd5",
      200: "#fed7aa",
      300: "#fdba74",
      400: "#fb923c",
      500: "#f97316",
      600: "#ea580c",
      700: "#c2410c",
      800: "#9a3412",
      900: "#7c2d12"
    },
    semantic: {
      success: "#10b981",
      error: "#dc2626",
      warning: "#f59e0b",
      info: "#3b82f6"
    },
    primary: {
      50: "#fff7ed",
      100: "#ffedd5",
      200: "#fed7aa",
      300: "#fdba74",
      400: "#fb923c",
      500: "#f97316",
      600: "#ea580c",
      700: "#c2410c",
      800: "#9a3412",
      900: "#7c2d12"
    },
    secondary: {
      50: "#fdf2f8",
      100: "#fce7f3",
      200: "#fbcfe8",
      300: "#f9a8d4",
      400: "#f472b6",
      500: "#ec4899",
      600: "#db2777",
      700: "#be185d",
      800: "#9d174d",
      900: "#831843"
    }
  }
};
var midnightTheme = {
  id: "midnight",
  name: "Midnight",
  description: "Dark theme with deep blues for late-night work",
  icon: "\u{1F319}",
  colors: {
    brand: {
      primary: "#818cf8",
      secondary: "#a78bfa",
      accent: "#34d399",
      danger: "#f87171",
      warning: "#fbbf24",
      info: "#60a5fa"
    },
    neutral: {
      50: "#1e1b4b",
      100: "#1e293b",
      200: "#334155",
      300: "#475569",
      400: "#64748b",
      500: "#94a3b8",
      600: "#cbd5e1",
      700: "#e2e8f0",
      800: "#f1f5f9",
      900: "#f8fafc"
    },
    semantic: {
      success: "#34d399",
      error: "#f87171",
      warning: "#fbbf24",
      info: "#60a5fa"
    },
    primary: {
      50: "#eef2ff",
      100: "#e0e7ff",
      200: "#c7d2fe",
      300: "#a5b4fc",
      400: "#818cf8",
      500: "#6366f1",
      600: "#4f46e5",
      700: "#4338ca",
      800: "#3730a3",
      900: "#312e81"
    },
    secondary: {
      50: "#f5f3ff",
      100: "#ede9fe",
      200: "#ddd6fe",
      300: "#c4b5fd",
      400: "#a78bfa",
      500: "#8b5cf6",
      600: "#7c3aed",
      700: "#6d28d9",
      800: "#5b21b6",
      900: "#4c1d95"
    }
  }
};
var rubyTheme = {
  id: "ruby",
  name: "Ruby",
  description: "Bold red theme for high-impact applications",
  icon: "\u{1F48E}",
  colors: {
    brand: {
      primary: "#dc2626",
      secondary: "#be123c",
      accent: "#f59e0b",
      danger: "#991b1b",
      warning: "#f59e0b",
      info: "#3b82f6"
    },
    neutral: {
      50: "#fef2f2",
      100: "#fee2e2",
      200: "#fecaca",
      300: "#fca5a5",
      400: "#f87171",
      500: "#ef4444",
      600: "#dc2626",
      700: "#b91c1c",
      800: "#991b1b",
      900: "#7f1d1d"
    },
    semantic: {
      success: "#10b981",
      error: "#991b1b",
      warning: "#f59e0b",
      info: "#3b82f6"
    },
    primary: {
      50: "#fef2f2",
      100: "#fee2e2",
      200: "#fecaca",
      300: "#fca5a5",
      400: "#f87171",
      500: "#ef4444",
      600: "#dc2626",
      700: "#b91c1c",
      800: "#991b1b",
      900: "#7f1d1d"
    },
    secondary: {
      50: "#fff1f2",
      100: "#ffe4e6",
      200: "#fecdd3",
      300: "#fda4af",
      400: "#fb7185",
      500: "#f43f5e",
      600: "#e11d48",
      700: "#be123c",
      800: "#9f1239",
      900: "#881337"
    }
  }
};
var agentNativeTheme = {
  id: "agent-native",
  name: "Agent Native",
  description: "The Agent Native corporate brand \u2014 dark obsidian surfaces with a platinum/silver accent system",
  icon: "\u2B21",
  colors: {
    brand: {
      primary: "#A8B0BC",
      // Platinum
      secondary: "#8891A0",
      // Silver
      accent: "#6B7B94",
      // Slate (interactive elements)
      danger: "#C96B6B",
      warning: "#D4A84A",
      info: "#6BA3D4"
    },
    neutral: {
      50: "#08090C",
      // Void — deepest background
      100: "#0E1014",
      // Obsidian — primary background
      200: "#15171C",
      // Graphite — nav, elevated bg
      300: "#1C1E25",
      // Surface — cards, panels
      400: "#24262E",
      // Surface 2 — inputs, hover
      500: "#2D3039",
      // Surface 3 — active states
      600: "#8A8F9E",
      // Grey 300 — secondary text
      700: "#B8BCC8",
      // Grey 200 — body text
      800: "#D5D8E0",
      // Grey 100 — high-contrast body
      900: "#ECEEF2"
      // White — primary text on dark
    },
    semantic: {
      success: "#5CC9A0",
      error: "#C96B6B",
      warning: "#D4A84A",
      info: "#6BA3D4"
    },
    primary: {
      50: "#1A2133",
      // Ink — deep blue-black
      100: "#2A3650",
      // Ink Light
      200: "#3A3E4A",
      // Grey 500
      300: "#5C6170",
      // Grey 400
      400: "#6B7B94",
      // Slate
      500: "#A8B0BC",
      // Platinum (brand.primary)
      600: "#8494AD",
      // Slate Light
      700: "#C4CAD4",
      // Silver Light
      800: "#D8DDE6",
      // Chrome
      900: "#ECEEF2"
      // White
    },
    secondary: {
      50: "#1A2133",
      // Ink
      100: "#24262E",
      // Surface 2
      200: "#3A3E4A",
      // Grey 500
      300: "#5C6170",
      // Grey 400
      400: "#6B7B94",
      // Slate
      500: "#8891A0",
      // Silver (brand.secondary)
      600: "#A8B0BC",
      // Platinum
      700: "#C4CAD4",
      // Silver Light
      800: "#D8DDE6",
      // Chrome
      900: "#ECEEF2"
      // White
    }
  }
};
var themes = {
  "peach-mint-cream": peachMintCreamTheme,
  indigo: indigoTheme,
  ocean: oceanTheme,
  forest: forestTheme,
  sunset: sunsetTheme,
  midnight: midnightTheme,
  ruby: rubyTheme,
  "agent-native": agentNativeTheme
};
function getTheme(themeId) {
  return themes[themeId] || themes["agent-native"];
}
function getAvailableThemes() {
  return Object.values(themes);
}
var ThemeContext = createContext(null);
function generateCSSVariables(theme) {
  const vars = [];
  Object.entries(theme.colors.brand).forEach(([key, value]) => {
    vars.push(`--color-brand-${key}: ${value};`);
  });
  Object.entries(theme.colors.neutral).forEach(([key, value]) => {
    vars.push(`--color-neutral-${key}: ${value};`);
  });
  Object.entries(theme.colors.semantic).forEach(([key, value]) => {
    vars.push(`--color-semantic-${key}: ${value};`);
  });
  Object.entries(theme.colors.primary).forEach(([key, value]) => {
    vars.push(`--color-primary-${key}: ${value};`);
  });
  Object.entries(theme.colors.secondary).forEach(([key, value]) => {
    vars.push(`--color-secondary-${key}: ${value};`);
  });
  Object.entries(tokens.typography.fontFamily).forEach(([key, value]) => {
    const varName = key === "sans" ? "body" : key === "mono" ? "mono" : key;
    vars.push(`--font-${varName}: ${value};`);
  });
  Object.entries(tokens.typography.fontSize).forEach(([key, value]) => {
    vars.push(`--font-size-${key}: ${value};`);
  });
  Object.entries(tokens.typography.fontWeight).forEach(([key, value]) => {
    vars.push(`--font-weight-${key}: ${value};`);
  });
  Object.entries(tokens.spacing).forEach(([key, value]) => {
    vars.push(`--spacing-${key}: ${value};`);
  });
  Object.entries(tokens.brandSpacing).forEach(([key, value]) => {
    vars.push(`--sp-${key}: ${value};`);
  });
  Object.entries(tokens.borderRadius).forEach(([key, value]) => {
    vars.push(`--radius-${key}: ${value};`);
  });
  Object.entries(tokens.brandRadius).forEach(([key, value]) => {
    vars.push(`--brand-radius-${key}: ${value};`);
  });
  Object.entries(tokens.shadows).forEach(([key, value]) => {
    vars.push(`--shadow-${key}: ${value};`);
  });
  Object.entries(tokens.brandShadows).forEach(([key, value]) => {
    vars.push(`--brand-shadow-${key}: ${value};`);
  });
  return `:root { ${vars.join(" ")} }`;
}
function ThemeProvider({ defaultThemeId = "agent-native", children }) {
  const [themeId, setThemeId] = React.useState(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("agentnative-theme") || defaultThemeId;
    }
    return defaultThemeId;
  });
  const theme = useMemo(() => getTheme(themeId), [themeId]);
  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("agentnative-theme", themeId);
      document.documentElement.setAttribute("data-theme", themeId);
    }
  }, [themeId]);
  const contextValue = useMemo(
    () => ({
      themeId,
      theme,
      availableThemes: themes,
      setTheme: (newThemeId) => {
        setThemeId(newThemeId);
      }
    }),
    [themeId, theme]
  );
  const cssVariables = useMemo(
    () => generateCSSVariables(theme),
    [theme]
  );
  return /* @__PURE__ */ jsxs(ThemeContext.Provider, { value: contextValue, children: [
    /* @__PURE__ */ jsx("style", { dangerouslySetInnerHTML: { __html: cssVariables } }),
    children
  ] });
}
function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}

export { ThemeProvider, __publicField, borderRadius, colors, getAvailableThemes, getTheme, spacing, themes, tokens, typography, useTheme };
